import { Terminal, Cpu, ShieldCheck, Database, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

interface AuthTerminalLoadingProps {
 title: string;
 subtitle: string;
 steps?: string[];
 status?: 'loading' | 'success' | 'error';
 error?: string;
}

export function AuthTerminalLoading({
 title,
 subtitle,
 steps = [
  'Establishing secure connection...',
  'Verifying session credentials...',
  'Syncing user profile data...',
  'Finalizing system configuration...',
 ],
 status = 'loading',
 error,
}: AuthTerminalLoadingProps) {
 const [currentStepIdx, setCurrentStepIdx] = useState(0);

 useEffect(() => {
  if (status !== 'loading') return;

  const interval = setInterval(() => {
   setCurrentStepIdx((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
  }, 800);

  return () => clearInterval(interval);
 }, [status, steps.length]);

 return (
  <div className='min-h-screen bg-background text-foreground font-sans p-4 md:p-6 flex flex-col items-center justify-center relative overflow-hidden'>
   {/* Background Grid */}
   <div className='fixed inset-0 z-0 pointer-events-none'>
    <div className='w-full h-full bg-[radial-gradient(#27272a_1px,transparent_1px)] [background-size:24px_24px] opacity-20'></div>
   </div>

   <div className='max-w-md w-full relative z-10'>
    <div className='flex items-center justify-between mb-8'>
     <div className='flex items-center gap-2 font-mono font-bold text-xl'>
      <Terminal className='w-6 h-6 text-primary' />
      <span>MOCK.SYSTEM</span>
     </div>
     <div className='font-mono text-[10px] text-muted-foreground animate-pulse'>
      PROTOCOL_STABLE // V2.0.4
     </div>
    </div>

    <div className='bg-background border border-border p-6 md:p-8 shadow-2xl relative'>
     {/* Progress Bar */}
     <div className='absolute top-0 left-0 w-full h-1 bg-primary/10'>
      <div
       className={`h-full bg-primary transition-all duration-1000 ${
        status === 'loading' ? 'animate-pulse' : ''
       }`}
       style={{
        width:
         status === 'loading'
          ? `${((currentStepIdx + 1) / steps.length) * 100}%`
          : '100%',
       }}
      />
     </div>

     <div className='space-y-6'>
      <div className='flex items-center gap-4 border-b border-border pb-4'>
       <div className='w-12 h-12 rounded-none bg-primary/10 flex items-center justify-center border border-primary/20'>
        {status === 'loading' && (
         <Loader2 className='w-6 h-6 text-primary animate-spin' />
        )}
        {status === 'success' && (
         <ShieldCheck className='w-6 h-6 text-primary' />
        )}
        {status === 'error' && <XCircle className='w-6 h-6 text-destructive' />}
       </div>
       <div>
        <h2 className='font-mono font-bold text-lg leading-tight uppercase tracking-tight'>
         {title}
        </h2>
        <p className='font-mono text-[10px] text-muted-foreground uppercase tracking-widest mt-1 italic'>
         {subtitle}
        </p>
       </div>
      </div>

      <div className='space-y-3 font-mono text-[11px] min-h-[120px]'>
       {steps.map((step, idx) => (
        <div
         key={idx}
         className={`flex items-start gap-3 transition-opacity duration-300 ${
          idx <= currentStepIdx ? 'opacity-100' : 'opacity-20'
         }`}
        >
         <span
          className={
           idx === currentStepIdx && status === 'loading'
            ? 'text-primary animate-pulse'
            : idx < currentStepIdx || status === 'success'
            ? 'text-primary'
            : 'text-muted-foreground'
          }
         >
          {idx < currentStepIdx || status === 'success'
           ? '[COMPLETE]'
           : idx === currentStepIdx && status === 'loading'
           ? '[RUNNING]'
           : '[PENDING]'}
         </span>
         <span
          className={
           idx === currentStepIdx && status === 'loading'
            ? 'text-foreground'
            : 'text-muted-foreground'
          }
         >
          &gt; {step.toUpperCase()}
         </span>
        </div>
       ))}

       {status === 'error' && (
        <div className='mt-4 p-3 border border-destructive/50 bg-destructive/10 text-destructive text-[10px] uppercase'>
         <div className='font-bold mb-1'>! ERROR_DETECTED</div>
         {error}
        </div>
       )}
      </div>

      <div className='pt-6 border-t border-border flex justify-between items-center font-mono text-[10px] text-muted-foreground'>
       <div className='flex items-center gap-2'>
        <Cpu className='w-3 h-3' />
        <span>CPU_LOAD: 12%</span>
       </div>
       <div className='flex items-center gap-2'>
        <Database className='w-3 h-3' />
        <span>ENCRYPTION: AES-256</span>
       </div>
      </div>
     </div>
    </div>

    <div className='mt-6 flex justify-center'>
     <div className='font-mono text-[9px] text-muted-foreground tracking-[0.2em] opacity-50 italic'>
      SECURITY_CLEARANCE_REQUIRED // AUTHORIZED_PERSONNEL_ONLY
     </div>
    </div>
   </div>
  </div>
 );
}

function XCircle({ className }: { className?: string }) {
 return (
  <svg
   xmlns='http://www.w3.org/2000/svg'
   width='24'
   height='24'
   viewBox='0 0 24 24'
   fill='none'
   stroke='currentColor'
   strokeWidth='2'
   strokeLinecap='round'
   strokeLinejoin='round'
   className={className}
  >
   <circle cx='12' cy='12' r='10' />
   <path d='m15 9-6 6' />
   <path d='m9 9 6 6' />
  </svg>
 );
}
